import { ButtonDropdown, ButtonV2, TooltipV2 } from '@cimplico/components';
import { WorksheetStatusEnum, WorksheetTypeEnum, Worksheet } from '../../../../types/worksheet';
import { useReviewWorksheets } from '../../../../queries/use-review-worksheets';
import { toastNotification } from '../../../../components/toast/Toast';
import { useIncludeWorksheets } from '../../../../queries/use-include-worksheets';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';
import { JournalStatusEnum } from '../../../../types/journal';
import { useState } from 'react';
import { useUpdateJournals } from '../../../../queries/use-update-journals';
import plural from '../../../../utilities/plural';
import queryClient from '../../../../query-client';
import { WorkpaperRoleEnum } from '../../../../types/workpaper';
import { getOutstandingConditionFigures } from '../../utils';
import { ArrowUturnLeftIcon, CheckIcon } from '@heroicons/react/24/solid';

type ReviewButtonProps = {
    worksheet: Worksheet;
    compactView?: boolean;
};

/**
 * Check if a worksheet can be reviewed based on a number of factors.
 *
 * @param worksheet Worksheet
 * @param role The current user's role
 * @returns A string or string[] explaining why the user cannot review
 */
function disabledReviewMessage(worksheet: Worksheet, role: WorkpaperRoleEnum): string {
    const canRoleReview = role === WorkpaperRoleEnum.APPROVER || role === WorkpaperRoleEnum.REVIEWER;

    if (worksheet.status === WorksheetStatusEnum.EXCLUDED) {
        return 'This worksheet is excluded.';
    }

    if (worksheet.status === WorksheetStatusEnum.REVIEWED) {
        return 'This worksheet is already reviewed.';
    }

    if (worksheet.status === WorksheetStatusEnum.IN_PROGRESS || worksheet.status === WorksheetStatusEnum.UNPREPARED) {
        const { reviewConditions } = getOutstandingConditionFigures(worksheet);

        if (reviewConditions > 0) {
            return 'Review conditions not satisfied.';
        }
    }

    if (!canRoleReview) {
        return 'This requires a reviewer role.';
    }

    return 'Continue working on this worksheet to make it awaiting review.';
}

function ReviewButton({ worksheet, compactView }: ReviewButtonProps): JSX.Element {
    const [isPostingAndReviewing, setIsPostingAndReviewing] = useState(false);
    const { readOnly, role } = useWorkpaperContext();
    const reviewQuery = useReviewWorksheets({ invalidateWorksheets: true });
    const postJournalsQuery = useUpdateJournals();
    const isReviewed = worksheet.status === WorksheetStatusEnum.REVIEWED;
    const includeQuery = useIncludeWorksheets();

    const roleCanReview = role === WorkpaperRoleEnum.REVIEWER || role === WorkpaperRoleEnum.APPROVER;
    const cannotReview = !roleCanReview || readOnly || reviewQuery.isLoading || worksheet.status !== WorksheetStatusEnum.AWAITING_REVIEW;

    // Check if the worksheet has any journals
    const isReconciliationWS = worksheet.type === WorksheetTypeEnum.RECONCILIATION;
    const hasDraftJournals = isReconciliationWS && worksheet.journals.some((j) => j.status === JournalStatusEnum.DRAFT);

    const reviewDisabledHint = disabledReviewMessage(worksheet, role);

    /**
     * Posts all draft journals then marks the worksheet as reviewed.
     */
    function postAndReview(): void {
        if (!isReconciliationWS) {
            return;
        }
        setIsPostingAndReviewing(true);

        const toPostIds = worksheet.journals.filter((j) => j.status === JournalStatusEnum.DRAFT).map((journal) => journal.id);

        postJournalsQuery.mutate(
            toPostIds.map((id) => ({ id, status: JournalStatusEnum.POSTED })),
            {
                onSuccess: () => {
                    review({ postedJournalsCount: toPostIds.length });
                },
                onError: (res) => {
                    if (toPostIds.length === 1) {
                        const errorMessage = res.response?.data.results.filter((r: { statusCode: number }) => r.statusCode === 400)[0].error
                            .fields.errorMessage;

                        toastNotification('error', 'Unable to review and post journal!', errorMessage);
                    } else {
                        const errorJournals = res.response?.data.results.filter((r: { statusCode: number }) => r.statusCode === 400).length;
                        const successJournals = res.response?.data.results.filter(
                            (r: { statusCode: number }) => r.statusCode === 200
                        ).length;

                        const errors = res.response?.data.results.filter((r: { statusCode: number }) => r.statusCode === 400);

                        toastNotification(
                            'error',
                            `Unable to review and post journals! ${successJournals} posted. ${errorJournals} failed to post.`,
                            errors.map(
                                (err: { error: { fields: { reference: string; errorMessage: string } } }) =>
                                    `${err.error.fields.reference} - ${err.error.fields.errorMessage}`
                            )
                        );
                    }
                },
            }
        );
    }

    /**
     * Marks the worksheet as reviewed.
     *
     * @param postedJournalsCount A count of how many journals were posted before reviewing to show to the user.
     */
    function review({ postedJournalsCount = 0 } = {}): void {
        reviewQuery.mutate([{ id: worksheet.id }], {
            onSuccess: async () => {
                toastNotification(
                    'success',
                    postedJournalsCount > 0
                        ? `Worksheet reviewed and ${postedJournalsCount} ${plural(postedJournalsCount, 'journal', 'journals')} posted!`
                        : 'Worksheet reviewed!'
                );
                await queryClient.invalidateQueries(['worksheets', worksheet.id]);
                setIsPostingAndReviewing(false);
            },
            onError: (e) => {
                const result = e.response?.data.results[0];
                toastNotification('error', 'Unable to review worksheet.', result.error.fields.errorMessage);
            },
        });
    }

    function unReview(): void {
        includeQuery.mutate([{ id: worksheet.id }], {
            onSuccess: () => {
                toastNotification('success', 'Worksheet un-reviewed!');
            },
        });
    }

    return isReviewed ? (
        <div>
            <ButtonV2
                size={compactView ? 'xs' : 'sm'}
                type="secondary"
                isLoading={includeQuery.isLoading}
                isDisabled={includeQuery.isLoading || readOnly}
                onClick={unReview}
                label={compactView ? undefined : 'Unreview'}
                requiresAltKey
                shortcutKey={cannotReview ? undefined : 'R'}
                leftIcon={compactView ? <ArrowUturnLeftIcon className="text-indigo-600" /> : undefined}
            />
        </div>
    ) : (
        <div>
            {hasDraftJournals && !compactView ? (
                <TooltipV2 message={reviewDisabledHint} disabled={!cannotReview}>
                    <ButtonDropdown
                        label="Review & Post"
                        size="xs"
                        variant="primary"
                        disabled={cannotReview || postJournalsQuery.isLoading}
                        onClick={postAndReview}
                        isLoading={postJournalsQuery.isLoading || reviewQuery.isLoading || isPostingAndReviewing}
                    >
                        <ButtonDropdown.SecondaryAction label="Review" onClick={review} disabled={cannotReview} />
                    </ButtonDropdown>
                </TooltipV2>
            ) : (
                <ButtonV2
                    size={compactView ? 'xs' : 'sm'}
                    type={compactView ? 'secondary' : 'primary'}
                    isDisabled={cannotReview}
                    onClick={(): void => review()}
                    isLoading={reviewQuery.isLoading}
                    shortcutKey={compactView ? undefined : 'R'}
                    label={compactView ? undefined : 'Review'}
                    requiresAltKey
                    disabledHint={reviewDisabledHint}
                    leftIcon={compactView ? <CheckIcon className="text-green-600" /> : undefined}
                />
            )}
        </div>
    );
}

export default ReviewButton;
